/**
 * `<ThemeProvider>` — Story 5.2.
 *
 * Owns theme state and is the single writer of the `<html data-theme="…">`
 * attribute, the `pvt:theme` localStorage key, and the `pvt_theme` cookie.
 *
 * Initial-value resolution priority (LOCKED, story Critical Scope Decision #1):
 *   1. `<html data-theme>` if literally `'dark'` or `'light'` — Story 5.5's
 *      CloudFront viewer-response Function replaces `__PVT_THEME__` with
 *      `dark`/`light` based on the `pvt_theme` cookie; honoring this
 *      attribute makes React state agree with first-paint and prevents a
 *      flash of unstyled / mis-themed content.
 *   2. `localStorage["pvt:theme"]` if literally `'dark'` or `'light'`.
 *   3. `document.cookie` `pvt_theme=dark|light`.
 *   4. `window.matchMedia('(prefers-color-scheme: light)').matches`. (Note
 *      the inversion: dark is the default per architecture §2.5 + UX §2.1,
 *      so we check for `light` to opt out.)
 *   5. Final fallback: `'dark'`.
 *
 * `setTheme(next)` write-through (LOCKED, AC #2):
 *   - React state update.
 *   - `<html data-theme>` set.
 *   - `localStorage["pvt:theme"]` (try/catch — Safari private mode safety).
 *   - Cookie `pvt_theme=<next>; Path=/; SameSite=Lax; Max-Age=31536000`,
 *     plus `Secure` ONLY when `window.location.protocol === 'https:'`
 *     (Critical Scope Decision #4 — dev-server compatibility).
 *
 * `prefers-color-scheme` listener (LOCKED, AC line 972, Critical Scope
 * Decision #3): registered on mount; fires `setTheme(...)` ONLY if the user
 * has never expressed a preference. "Never expressed" means BOTH
 * `localStorage["pvt:theme"]` was empty at mount AND no in-session toggle
 * has flipped `userExplicitlyChose.current` to `true`.
 *
 * Forward references (Story 5.2 spec, lines 257-262):
 *   - Story 5.5: edge function sets `<html data-theme>` from cookie.
 *   - Story 5.6: defines `--brand-primary` for the toggle's focus ring.
 *   - Story 7.12: admin Cognito `custom:theme` reconciliation.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { ThemeContext } from './ThemeContext';
import type { Theme } from './ThemeContext';

const LOCAL_STORAGE_KEY = 'pvt:theme';
const COOKIE_NAME = 'pvt_theme';
const COOKIE_MAX_AGE_SECONDS = 31_536_000; // 1 year (365 * 24 * 3600).

/**
 * Reads `<html data-theme>` and returns the value ONLY if it is the literal
 * `'dark'` or `'light'` string. Anything else (null, empty, the un-replaced
 * `__PVT_THEME__` literal from Story 5.5's edge function failure path) returns
 * `null`. Story 5.1 review M1 lesson: never trust `getAttribute` casts.
 */
function readHtmlDataTheme(): Theme | null {
  if (typeof document === 'undefined') return null;
  const attr = document.documentElement.getAttribute('data-theme');
  return attr === 'dark' || attr === 'light' ? attr : null;
}

/**
 * Reads `localStorage[pvt:theme]`. Returns `null` for any non-canonical value
 * (empty, missing, garbage written by another tab / extension, or a Safari
 * private-mode / quota-exceeded throw). Wrapped in try/catch because
 * accessing localStorage itself throws in some sandboxed contexts.
 */
function readLocalStorageTheme(): Theme | null {
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw === 'dark' || raw === 'light' ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Parses `document.cookie` for `pvt_theme=dark|light`. Cookie format from
 * MDN: a `; `-separated list of `name=value` pairs (no attributes — those
 * are stripped on the read side per Critical Scope Decision #5). Whitespace
 * tolerance: trims each pair before splitting.
 */
function readCookieTheme(): Theme | null {
  if (typeof document === 'undefined') return null;
  const cookieString = document.cookie;
  if (!cookieString) return null;
  const pairs = cookieString.split(';');
  for (const raw of pairs) {
    const trimmed = raw.trim();
    const eqIdx = trimmed.indexOf('=');
    if (eqIdx === -1) continue;
    const name = trimmed.slice(0, eqIdx);
    const value = trimmed.slice(eqIdx + 1);
    if (name === COOKIE_NAME && (value === 'dark' || value === 'light')) {
      return value;
    }
  }
  return null;
}

/**
 * Reads `prefers-color-scheme: light`. Defaults to `'dark'` for any failure
 * path (no matchMedia, query mismatch, etc.). Note the inversion: we check
 * `light` because the project default is dark.
 */
function readPrefersColorScheme(): Theme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'dark';
  }
  try {
    return window.matchMedia('(prefers-color-scheme: light)').matches
      ? 'light'
      : 'dark';
  } catch {
    return 'dark';
  }
}

/**
 * Resolves the initial theme on mount per the LOCKED priority chain. Pure
 * function of the document/window state at call time; called exactly once
 * via `useState`'s lazy initializer.
 */
function resolveInitialTheme(): Theme {
  const fromHtml = readHtmlDataTheme();
  if (fromHtml) return fromHtml;
  const fromLocal = readLocalStorageTheme();
  if (fromLocal) return fromLocal;
  const fromCookie = readCookieTheme();
  if (fromCookie) return fromCookie;
  return readPrefersColorScheme();
}

/**
 * Builds the `Set-Cookie`-style cookie string to write via `document.cookie`.
 * `Secure` is conditional on HTTPS (Critical Scope Decision #4): browsers
 * silently reject `Secure` cookies on `http://` connections, including
 * Vite's `http://localhost:5173` dev server. `SameSite=Lax` is adequate
 * (no cross-site security boundary to defend; the cookie is not auth state).
 * `HttpOnly` is intentionally omitted (architecture §2.5 line 244 — the SPA
 * reads the cookie as an initial-value fallback).
 */
function buildCookieString(value: Theme): string {
  const isHttps =
    typeof window !== 'undefined' && window.location.protocol === 'https:';
  const attrs = [
    `${COOKIE_NAME}=${value}`,
    'Path=/',
    'SameSite=Lax',
    `Max-Age=${COOKIE_MAX_AGE_SECONDS}`,
  ];
  if (isHttps) attrs.push('Secure');
  return attrs.join('; ');
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // useState lazy initializer — runs exactly once on mount, reads from
  // document/window which are stable for the lifetime of the page.
  const [theme, setThemeState] = useState<Theme>(() => resolveInitialTheme());

  /**
   * Critical Scope Decision #6: the user-explicit-choice flag is a `useRef`,
   * not a `useState`. The flag is not rendered into JSX; mutating
   * `.current` doesn't (and shouldn't) trigger a re-render. Initial value
   * is `localStorage["pvt:theme"] !== null` — a stored preference IS an
   * explicit choice from a prior session (Critical Scope Decision #3).
   */
  const userExplicitlyChose = useRef<boolean>(readLocalStorageTheme() !== null);

  /**
   * Single source of truth for the persistence write-through. Called by both
   * `setTheme` (from explicit consumers like the toggle) and `toggleTheme`
   * (sugar that flips the current theme). Centralizing here means future
   * changes to the persistence path (added stores, additional cookie attrs,
   * etc.) only need to land in one site. Code Review M1 — 2026-05-16.
   *
   * NOTE: this helper does NOT call `setThemeState`. The caller is expected
   * to update React state via `setThemeState` separately so it can use the
   * updater-function form (`setThemeState((prev) => ...)`) for the toggle
   * path that needs the previous value. The other side effects are
   * idempotent in `prev`/`next`, so doing them outside the updater is safe.
   */
  const persistTheme = useCallback((next: Theme) => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', next);
    }
    try {
      window.localStorage.setItem(LOCAL_STORAGE_KEY, next);
    } catch {
      // Safari private mode + storage-quota: never throw out of a click handler.
    }
    if (typeof document !== 'undefined') {
      document.cookie = buildCookieString(next);
    }
    userExplicitlyChose.current = true;
  }, []);

  // Mirror `theme` into a ref so `toggleTheme` can read the current value
  // synchronously without closing over a stale render's `theme`. The setState
  // updater form would also work, but doing side effects inside an updater
  // is impure (React StrictMode invokes updaters twice in dev) — keeping the
  // ref-mirror lets us do all writes at the call site, outside any updater.
  const themeRef = useRef<Theme>(theme);
  useEffect(() => {
    themeRef.current = theme;
  }, [theme]);

  const setTheme = useCallback(
    (next: Theme) => {
      setThemeState(next);
      persistTheme(next);
    },
    [persistTheme],
  );

  const toggleTheme = useCallback(() => {
    // Read current theme from the ref, not the closure: the ref is kept in
    // sync via the effect above, so it reflects the latest committed state
    // even if `toggleTheme` was created in an earlier render.
    const next: Theme = themeRef.current === 'dark' ? 'light' : 'dark';
    setThemeState(next);
    persistTheme(next);
  }, [persistTheme]);

  // First-paint sync of `<html data-theme>` for the case where the initial
  // value was resolved from localStorage / cookie / matchMedia (i.e., NOT
  // from `<html data-theme>` itself, because that path is already correct).
  // Without this, a user with localStorage='light' and `<html data-theme>=dark`
  // (the index.html literal) would briefly render dark before any toggle.
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', theme);
    }
    // Run only on mount — `setTheme` writes the attribute on every change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // matchMedia change listener — first-visit-only auto-flip per AC #2 +
  // Critical Scope Decision #3.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (event: MediaQueryListEvent) => {
      // Skip if the user has ever expressed a preference (this session OR
      // any prior session — the prior-session path is the localStorage
      // check baked into userExplicitlyChose.current's initial value).
      if (userExplicitlyChose.current) return;
      const next: Theme = event.matches ? 'dark' : 'light';
      setThemeState(next);
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme', next);
      }
      // NOTE: matchMedia-driven flips do NOT write localStorage/cookie —
      // those writes are reserved for explicit user actions. If we wrote
      // them here, the very next mount would treat the OS-derived value
      // as "explicit choice" and pin to it, defeating the listener.
    };

    // Modern browsers: addEventListener; the test stub provides this too.
    mediaQuery.addEventListener('change', handleChange);
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  // Stabilize the context value object so consumers don't re-render on
  // every parent render. setTheme / toggleTheme are already useCallback'd.
  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme }),
    [theme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
